import { ConfigProvider, App as AntApp, Result, Button } from 'antd';
import zhTW from 'antd/locale/zh_TW';
import enUS from 'antd/locale/en_US';
import { QueryClientProvider, useQuery } from '@tanstack/react-query';
import { lazy, Suspense, useEffect, useState } from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { RouterProvider } from 'react-router-dom';
import { api } from '@/api/services';
import { queryClient } from '@/api/query-client';
import i18n from '@/i18n';
import { router } from './router';
import { useSessionStore } from '@/stores/session-store';
import { ApiErrorState } from '@/components/feedback/ApiErrorState';
import { appTheme } from '@/theme/theme';

const LoginPage = lazy(() => import('@/features/auth/LoginPage'));

function Bootstrap() {
  const { i18n: currentI18n } = useTranslation();
  const [authPath, setAuthPath] = useState(() => window.location.pathname);
  const setMe = useSessionStore((state) => state.setMe);
  const status = useSessionStore((state) => state.status);
  const establishSession = useSessionStore((state) => state.establishSession);
  const clearSession = useSessionStore((state) => state.clearSession);
  const showingLogin = status === 'anonymous' || status === 'expired'
    || (authPath === '/login' && status !== 'authenticated');
  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: ({ signal }) => api.me(signal),
    enabled: status === 'authenticated',
    retry: false,
  });

  useEffect(() => {
    const syncPath = () => setAuthPath(window.location.pathname);
    window.addEventListener('popstate', syncPath);
    return () => window.removeEventListener('popstate', syncPath);
  }, []);

  useEffect(() => {
    if ((status === 'anonymous' || status === 'expired') && authPath !== '/login') {
      void router.navigate('/login', { replace: true });
      setAuthPath('/login');
    }
  }, [authPath, status]);

  useEffect(() => {
    if (status !== 'bootstrapping') return;
    let active = true;
    void api.refresh().then(({ accessToken }) => {
      if (!active) return;
      establishSession(accessToken);
      if (window.location.pathname === '/login') {
        void router.navigate('/dashboard', { replace: true });
        setAuthPath('/dashboard');
      }
    }).catch(() => {
      if (active) clearSession('anonymous');
    });
    return () => { active = false; };
  }, [clearSession, establishSession, status]);

  useEffect(() => {
    if (meQuery.data) setMe(meQuery.data);
  }, [meQuery.data, setMe]);

  if (status === 'bootstrapping' || status === 'refreshing') {
    return <div className="app-bootstrap" role="status" aria-live="polite"><div className="bootstrap-logo"><img src="/images/taiwan-einvoice-mark-v2.svg" alt="" /></div><div className="bootstrap-line" /><span>正在恢復安全工作階段…</span></div>;
  }

  if (showingLogin) {
    return (
      <ConfigProvider locale={currentI18n.language === 'en' ? enUS : zhTW} theme={appTheme}>
        <Suspense fallback={<div className="app-bootstrap"><div className="bootstrap-logo"><img src="/images/taiwan-einvoice-mark-v2.svg" alt="" /></div><div className="bootstrap-line" /></div>}>
          <LoginPage onLogin={(accessToken) => {
            queryClient.clear();
            establishSession(accessToken);
            void router.navigate('/dashboard', { replace: true });
            setAuthPath('/dashboard');
          }} />
        </Suspense>
      </ConfigProvider>
    );
  }
  if (meQuery.isLoading) {
    return <div className="app-bootstrap"><div className="bootstrap-logo"><img src="/images/taiwan-einvoice-mark-v2.svg" alt="" /></div><div className="bootstrap-line" /><span>正在建立安全操作情境…</span></div>;
  }
  if (meQuery.isError) {
    return <div className="bootstrap-error"><ApiErrorState error={meQuery.error} onRetry={() => void meQuery.refetch()} /></div>;
  }
  if (!meQuery.data?.context.organization) {
    return <Result status="warning" title="尚未選取操作情境" extra={<Button type="primary">選取組織</Button>} />;
  }
  return (
    <ConfigProvider
      locale={currentI18n.language === 'en' ? enUS : zhTW}
      theme={appTheme}
    >
      <RouterProvider router={router} />
    </ConfigProvider>
  );
}

export default function App() {
  return (
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <AntApp>
          <Bootstrap />
        </AntApp>
      </QueryClientProvider>
    </I18nextProvider>
  );
}
